import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { AuditHistory } from "@/components/audit/AuditHistory";
import {
  LockedAutocomplete,
  type AutocompleteOption,
} from "@/components/LockedAutocomplete";
import { api } from "@/lib/apiClient";
import { UNIT_LABELS, unitNights } from "@/lib/articleConstants";
import { formatCurrency, grossAmountFromNet, netAmountFromGross } from "@/lib/money";
import { ArticleBatchesSection } from "@/pages/stammdaten/ArticleBatchesSection";
import { ArticlePricesSection } from "@/pages/stammdaten/ArticlePricesSection";
import { ArticleShopContentSection } from "@/pages/stammdaten/ArticleShopContentSection";
import { ProductBarcodeLookupField } from "@/components/pos/ProductBarcodeLookupField";
import {
  accountSource,
  accountToOption,
  supplierSource,
  supplierToOption,
} from "@/lib/autocompleteSources";
import {
  AccountTaxRateNotice,
  accountHasTaxRate,
} from "@/pages/stammdaten/AccountTaxRateNotice";
import type { Account, Article, Supplier } from "@/types/camping";

interface ArticleFormState {
  name: string;
  unit: string;
  tax_rate_percent: string;
  description: string;
  account_id: string;
  supplier_id: string;
  barcode: string;
  purchase_price: string;
  track_inventory: boolean;
  requires_pickup_receipt: boolean;
  for_guest_shop: boolean;
  shop_description: string;
  shop_image_url: string | null;
  is_active: boolean;
}

function articleToForm(article: Article): ArticleFormState {
  const purchaseGross =
    article.purchase_price != null
      ? grossAmountFromNet(article.purchase_price, article.tax_rate_percent)
      : null;
  return {
    name: article.name,
    unit: article.unit,
    tax_rate_percent: article.tax_rate_percent,
    description: article.description ?? "",
    account_id: article.account_id ?? "",
    supplier_id: article.supplier_id ?? "",
    barcode: article.barcode ?? "",
    purchase_price: purchaseGross != null ? String(purchaseGross) : "",
    track_inventory: article.track_inventory,
    requires_pickup_receipt: article.requires_pickup_receipt,
    for_guest_shop: article.for_guest_shop,
    shop_description: article.shop_description ?? "",
    shop_image_url: article.shop_image_url,
    is_active: article.is_active,
  };
}

export interface EditArticleModalProps {
  open: boolean;
  article: Article | null;
  onClose: () => void;
  onSaved: () => void;
  canEdit: boolean;
}

export function EditArticleModal({
  open,
  article,
  onClose,
  onSaved,
  canEdit,
}: EditArticleModalProps) {
  const [form, setForm] = useState<ArticleFormState | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierOption, setSupplierOption] = useState<AutocompleteOption<Supplier> | null>(null);
  const [accountOption, setAccountOption] = useState<AutocompleteOption<Account> | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<"master" | "prices" | "more" | "history">("master");

  const loadSupplierOptions = useCallback(
    (query: string) => supplierSource(suppliers, query),
    [suppliers],
  );
  const loadAccountOptions = useCallback(
    (query: string) => accountSource(accounts, query),
    [accounts],
  );

  function resolveTaxRate(accountId: string, fallback: string): number {
    const account = accounts.find((item) => item.id === accountId) ?? null;
    if (accountHasTaxRate(account)) return Number(account?.tax_rate_percent);
    if (!accountId && article?.account_id) return 0;
    const fallbackRate = Number(fallback);
    return Number.isFinite(fallbackRate) ? fallbackRate : 0;
  }

  useEffect(() => {
    if (!open || !article) return;
    setForm(articleToForm(article));
    setError(null);
    setSubmitting(false);
    setTab("master");
    api
      .get<Account[]>("/api/v1/accounts?include_inactive=true")
      .then((list) => {
        setAccounts(list);
        const account = list.find((a) => a.id === article.account_id);
        setAccountOption(account ? accountToOption(account) : null);
      })
      .catch(() => {
        setAccounts([]);
        setAccountOption(null);
      });
    api
      .get<Supplier[]>("/api/v1/suppliers?include_inactive=true")
      .then((list) => {
        setSuppliers(list);
        const supplier = list.find((s) => s.id === article.supplier_id);
        setSupplierOption(supplier ? supplierToOption(supplier) : null);
      })
      .catch(() => {
        setSuppliers([]);
        setSupplierOption(null);
      });
  }, [open, article]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!article || !form || !canEdit) return;
    if (!form.name.trim()) {
      setTab("master");
      setError("Bitte eine Bezeichnung eingeben.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const taxRate = resolveTaxRate(form.account_id, form.tax_rate_percent);
      await api.patch(`/api/v1/articles/${article.id}`, {
        name: form.name.trim(),
        unit: form.unit,
        description: form.description.trim() || null,
        account_id: form.account_id || null,
        supplier_id: form.supplier_id || null,
        barcode: form.barcode.trim() || null,
        purchase_price: netAmountFromGross(form.purchase_price, taxRate),
        track_inventory: form.track_inventory,
        requires_pickup_receipt: form.requires_pickup_receipt,
        for_guest_shop: form.for_guest_shop,
        shop_description: form.for_guest_shop ? form.shop_description.trim() || null : null,
        shop_image_url: form.for_guest_shop ? form.shop_image_url : null,
        is_active: form.is_active,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Speichern");
    } finally {
      setSubmitting(false);
    }
  }

  if (!article || !form) return null;

  const selectedAccount = accounts.find((item) => item.id === form.account_id) ?? null;
  const taxRate = resolveTaxRate(form.account_id, form.tax_rate_percent);
  const purchaseNetPreview = netAmountFromGross(form.purchase_price, taxRate);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${article.article_number} – ${article.name}`}
      description={canEdit ? "Stammdaten, Buchungskonto und Preise" : "Artikeldetails (nur Lesen)"}
      size="xl"
      footer={
        canEdit && (tab === "master" || tab === "more") ? (
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
              Abbrechen
            </Button>
            <Button type="submit" form="edit-article-form" loading={submitting}>
              Stammdaten speichern
            </Button>
          </div>
        ) : (
          <div className="flex justify-end">
            <Button type="button" variant="secondary" onClick={onClose}>
              Schließen
            </Button>
          </div>
        )
      }
    >
      <div className="space-y-5">
        <div className="-mx-1 overflow-x-auto px-1">
        <div className="eh-segmented eh-segmented--md min-w-max" role="tablist" aria-label="Artikelbereiche">
          {(
            [
              ["master", "Stammdaten"],
              ["prices", "Preise"],
              ["more", "Lager & Shop"],
              ["history", "Verlauf"],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={clsx("eh-segmented-item", tab === id && "is-active")}
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
        </div>
        </div>
      <form id="edit-article-form" onSubmit={(e) => void onSubmit(e)} className="space-y-6">
        <div className={clsx("space-y-6", tab !== "master" && "hidden")}>
        {canEdit ? (
          <ProductBarcodeLookupField
            barcode={form.barcode}
            onBarcodeChange={(barcode) => setForm((f) => (f ? { ...f, barcode } : f))}
            onLookupResult={(result) =>
              setForm((f) =>
                f
                  ? {
                      ...f,
                      barcode: result.barcode,
                      name: result.name ?? f.name,
                    }
                  : f,
              )
            }
            disabled={submitting}
          />
        ) : form.barcode ? (
          <p className="text-sm text-label-secondary">
            Barcode: <span className="font-mono text-label">{form.barcode}</span>
          </p>
        ) : null}
        <fieldset className="space-y-4" disabled={!canEdit}>
          <legend className="sr-only">Stammdaten</legend>
          <p className="text-sm text-label-secondary">
            Artikelnummer:{" "}
            <span className="font-medium text-label">{article.article_number}</span>
            {article.track_inventory ? (
              <>
                {" "}
                · Lagerbestand: <span className="font-medium text-label">{article.stock_quantity}</span>
              </>
            ) : null}
          </p>
          <label className="flex flex-col gap-1 text-sm">
            Bezeichnung
            <input
              className="eh-input"
              value={form.name}
              onChange={(e) => setForm((f) => (f ? { ...f, name: e.target.value } : f))}
            />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm">
              Einheit
              <select
                className="eh-select-input"
                value={form.unit}
                onChange={(e) => setForm((f) => (f ? { ...f, unit: e.target.value } : f))}
              >
                {Object.entries(UNIT_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              {unitNights(form.unit) != null ? (
                <span className="text-xs text-label-secondary">
                  1 {UNIT_LABELS[form.unit]} = {unitNights(form.unit)} Nächte (Referenz für
                  Teilberechnungen)
                </span>
              ) : null}
            </label>
            <label className="flex items-center gap-2 self-end pb-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((f) => (f ? { ...f, is_active: e.target.checked } : f))}
              />
              Aktiv
            </label>
          </div>
          <label className="flex flex-col gap-1 text-sm">
            EK brutto (Einkauf)
            <input
              type="number"
              step="0.01"
              min="0"
              className="eh-input"
              value={form.purchase_price}
              onChange={(e) => setForm((f) => (f ? { ...f, purchase_price: e.target.value } : f))}
            />
            {purchaseNetPreview != null ? (
              <span className="text-xs text-label-secondary">
                Netto (Buchhaltung):{" "}
                <span className="font-medium tabular-nums text-label">
                  {formatCurrency(purchaseNetPreview)}
                </span>
              </span>
            ) : null}
          </label>
          <label className="flex flex-col gap-1 text-sm sm:col-span-2">
            Beschreibung
            <textarea
              rows={2}
              className="eh-input"
              value={form.description}
              onChange={(e) => setForm((f) => (f ? { ...f, description: e.target.value } : f))}
            />
          </label>
        </fieldset>

        <fieldset className="space-y-3" disabled={!canEdit}>
          <legend className="text-sm font-semibold text-label">Buchungskonto</legend>
          <LockedAutocomplete
            label="Buchungskonto"
            disabled={!canEdit}
            placeholder="Kontonummer oder Bezeichnung suchen…"
            value={accountOption}
            loadOptions={loadAccountOptions}
            onChange={(option) => {
              setAccountOption(option);
              setForm((f) =>
                f
                  ? {
                      ...f,
                      account_id: option?.value.id ?? "",
                      tax_rate_percent: accountHasTaxRate(option?.value)
                        ? String(Number(option?.value.tax_rate_percent))
                        : f.tax_rate_percent,
                    }
                  : f,
              );
            }}
          />
          <AccountTaxRateNotice account={selectedAccount} />
        </fieldset>
        </div>

        <div className={clsx("space-y-4", tab !== "more" && "hidden")}>
        <fieldset className="space-y-3" disabled={!canEdit}>
          <legend className="text-sm font-semibold text-label">Lager und Shop</legend>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.track_inventory}
              onChange={(e) =>
                setForm((f) => (f ? { ...f, track_inventory: e.target.checked } : f))
              }
            />
            Chargenführung / Lager
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.requires_pickup_receipt}
              onChange={(e) =>
                setForm((f) => (f ? { ...f, requires_pickup_receipt: e.target.checked } : f))
              }
            />
            Abholbeleg nach Verkauf drucken
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.for_guest_shop}
              onChange={(e) =>
                setForm((f) => (f ? { ...f, for_guest_shop: e.target.checked } : f))
              }
            />
            Im Gäste-Shop (Vorbestellung)
          </label>
          {form.for_guest_shop ? (
            <ArticleShopContentSection
              value={{
                shop_description: form.shop_description,
                shop_image_url: form.shop_image_url,
              }}
              onChange={(shopContent) =>
                setForm((f) =>
                  f
                    ? {
                        ...f,
                        shop_description: shopContent.shop_description,
                        shop_image_url: shopContent.shop_image_url,
                      }
                    : f,
                )
              }
              articleName={form.name}
              canEdit={canEdit}
              onError={setError}
            />
          ) : null}
          <LockedAutocomplete
            className="sm:col-span-2"
            label="Lieferant (optional)"
            disabled={!canEdit}
            placeholder="Lieferant suchen…"
            value={supplierOption}
            loadOptions={loadSupplierOptions}
            onChange={(option) => {
              setSupplierOption(option);
              setForm((f) => (f ? { ...f, supplier_id: option?.value.id ?? "" } : f));
            }}
          />
        </fieldset>
        {form.track_inventory || article.track_inventory ? (
          <ArticleBatchesSection
            articleId={article.id}
            taxRatePercent={String(taxRate)}
            canEdit={canEdit}
            active={open && tab === "more"}
          />
        ) : (
          <p className="text-sm text-label-secondary">
            Chargen und Lagerbestand erscheinen hier, sobald die Chargenführung aktiv ist.
          </p>
        )}
        </div>

        {error ? <p className="text-sm text-status-red">{error}</p> : null}
      </form>

        <div className={clsx(tab !== "prices" && "hidden")}>
        <ArticlePricesSection
          articleId={article.id}
          taxRatePercent={String(taxRate)}
          canEdit={canEdit}
          onSaved={onSaved}
        />
        </div>

        <div className={clsx(tab !== "history" && "hidden")}>
        <AuditHistory entityType="article" entityId={article.id} active={open && tab === "history"} />
        </div>
      </div>
    </Modal>
  );
}
